<script lang="ts" setup>
import { computed } from 'vue';
import { IconCircleCheckFilled, IconAlertCircleFilled } from '@/icons';

export type AiAlertVariant = 'normal' | 'warning' | 'critical';

export interface AiAlertItem {
  label: string;
  value: string;
}

interface Props {
  variant?: AiAlertVariant;
  title: string;
  tagText?: string;
  items?: AiAlertItem[];
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'normal',
  tagText: undefined,
  items: () => [],
});

const variantConfig = computed(() => {
  const map: Record<AiAlertVariant, {
    border: string;
    text: string;
    shade: string;
    icon: typeof IconCircleCheckFilled;
  }> = {
    normal: {
      border: 'var(--ui2-shade-pine-forest-8, rgba(34, 107, 108, 0.08))',
      text: 'var(--ui2-data-pine-forest)',
      shade: 'var(--ui2-shade-pine-forest-8, rgba(34, 107, 108, 0.08))',
      icon: IconCircleCheckFilled,
    },
    warning: {
      border: 'var(--ui2-shade-desert-sunrise-80, rgba(228, 161, 75, 0.8))',
      text: 'var(--ui2-primitive-desert-sunrise-500)',
      shade: 'var(--ui2-shade-desert-sunrise-8, rgba(228, 161, 75, 0.08))',
      icon: IconAlertCircleFilled,
    },
    critical: {
      border: 'var(--ui2-shade-ember-rose-80, rgba(230, 57, 70, 0.8))',
      text: 'var(--ui2-primitive-ember-rose-500)',
      shade: 'var(--ui2-shade-ember-rose-8, rgba(230, 57, 70, 0.08))',
      icon: IconAlertCircleFilled,
    },
  };
  return map[props.variant];
});
</script>

<template>
  <div
    :class="$style.root"
    :style="{ borderColor: variantConfig.border }"
    data-ui-component="UiAiAlert"
  >
    <div :class="$style.header">
      <div :class="$style.title">{{ title }}</div>
      <span
        v-if="tagText"
        :class="$style.tag"
        :style="{ color: variantConfig.text, backgroundColor: variantConfig.shade }"
      >
        <component
          :is="variantConfig.icon"
          :style="{ color: variantConfig.text }"
          width="14"
          height="14"
          aria-hidden="true"
        />
        {{ tagText }}
      </span>
    </div>

    <div v-if="items.length" :class="$style.dataRow">
      <template v-for="(item, idx) in items" :key="idx">
        <span v-if="idx > 0" :class="$style.bullet">•</span>
        <span :class="$style.dataItem">
          <span :class="$style.dataLabel">{{ item.label }}</span>
          <span :class="$style.dataValue" :style="{ color: variantConfig.text }">{{ item.value }}</span>
        </span>
      </template>
    </div>
  </div>
</template>

<style lang="scss" module>
.root {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid;
  background: var(--ui2-background-primary, #fff);
  width: 100%;
  box-sizing: border-box;
  font-family: 'Poppins', sans-serif;
}

.header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--ui2-content-primary, #010000);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
  flex: none;
}

.dataRow {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px solid var(--ui2-stroke-tertiary, #f0f1f4);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  overflow: hidden;
  white-space: nowrap;
}

.bullet {
  color: var(--ui2-content-tertiary, #978f91);
  flex: none;
}

.dataItem {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex: none;
}

.dataLabel {
  color: var(--ui2-content-tertiary, #978f91);
}

.dataValue {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
